<template>
	<t-popover
		:showPopover="popoverIsVisible"
		:marginY="6"
		:style="getStyle"
		position="bottom"
		:min-width="150"
		relative
		v-bind="$attrs"
	>
		<t-input-wrapper
			slot="target"
			:label="label"
			:description="description"
			:hint="hint"
			:isInvalid="isInvalid"
			:validationMessage="validationMessage"
			:warningMessage="warningMessage"
		>
			<t-button
				ref="input"
				:width="width"
				:appearance="appearance"
				:intent="intent"
				:size="size"
				:disabled="disabled"
				preventFocusOnClick
				v-bind="$attrs"
				iconAfter="caret-down"
				iconSize="sm"
				@click="onClick"
				@focus="onFocus"
				@blur="onBlur($event)"
			>
				<!-- @slot Slot displayed value on select dropdown. Defaults to current selected -->
				<slot
					name="displayedValue"
					:popoverShow="popoverIsVisible"
				>
					{{ getDisplayedValue }}
				</slot>
			</t-button>
		</t-input-wrapper>

		<t-menu v-if="items">
			<t-menu-options-group
				ref="menuOptions"
				v-slot="slotProps"
				:title="title"
				:items="items"
				:selectedItem="selectedItem"
				:selectedItems="selectedItems"
				enableFocusNextOnKeyboardSelect
				groupStyle="combobox"
				@change="onChange"
			>
				<slot
					name="optionItem"
					:item="slotProps.item"
					:isSelected="slotProps.isSelected"
				>
					<t-menu-combobox-option-item
						:value="slotProps.item"
						:selected="slotProps.isSelected"
						:focused="slotProps.isFocused"
					>
						<slot
							name="optionItemValue"
							:item="slotProps.item"
						>
							{{
								displayName
									? slotProps.item[displayName]
									: slotProps.item
							}}
						</slot>
					</t-menu-combobox-option-item>
				</slot>
			</t-menu-options-group>
		</t-menu>
	</t-popover>
</template>

<script>
import TButton from '../buttons/TButton';
import TPopover from '../overlays/TPopover';
import {
	TMenu, TMenuOptionsGroup, TMenuComboboxOptionItem
} from '../menu';
import formMixin from './formMixin';
import popoverMixin from './popoverMixin';
import inputWrapperMixin from './inputWrapperMixin';
import TInputWrapper from './TInputWrapper';
export default {
	name: 'TSelect',
	components: { TButton, TInputWrapper, TPopover, TMenu, TMenuOptionsGroup, TMenuComboboxOptionItem },
	mixins: [ formMixin, inputWrapperMixin, popoverMixin ],
	inheritAttrs: false,
	props: {
		displayName: String,
		title: String,
		appearance: String,
		intent: String,
		tabindex: [Number, String],
		showPopoverOnFocus: {
			type: Boolean,
			default: false
		},
		items: {
			type: Array,
			default: () => []
		},
		multiselect: Boolean,
		selectedItem: [ String,Object,Number,Boolean ],
		selectedItems: Array,
		enableFocusNextOnKeyboardSelect: Boolean,
	},
	computed: {
		getDisplayedValue() {
			return this.selectedItem
				? this.getSelectedItemValue
				: this.placeholder;
		},
		getSelectedItemValue() {
			return this.displayName
				? this.selectedItem[this.displayName]
				: this.selectedItem;
		}
	},
	methods: {
		onSelect(value) {

			this.$refs.input.$el.focus();

			if (this.multiselect) {
				this.$emit('update:selectedItems', value);
			} else {
				this.$emit('update:selectedItem', value);
			}
			this.$emit('change', value);
			this.$emit('select', value);
		},
		onChange(value) {
			this.$refs.input.$el.focus();
			this.popoverSetHidden();

			this.$refs.input.$el.focus();

			this.onSelect(value);
		},
		onClick() {
			this.popoverToggle();
			this.$refs.input.$el.focus();
		},
		onBlur(event) {
			if (
				event.relatedTarget
				&& this.$refs.menuOptions
				&& this.$refs.menuOptions.$el
				&& this.$refs.menuOptions.$el.contains(event.relatedTarget)
			) {
				event.target.focus();
			} else {
				this.popoverSetHidden();
			}
		},
		onFocus() {
			if (this.showPopoverOnFocus)
				this.popoverSetVisible();
		},
	}
};
</script>

<docs>
## Examples
```jsx noeditor
new Vue({
	data(){
		return {
			fruits: ['Apple', 'Orange', 'Lemon', 'Strawberry'],
			selectedFruit: 'Apple',
		}
	},
	template: `
		<div style="width:100%;display: inline-grid; grid-row-gap: 10px; grid-template-rows: repeat(4, auto);">
			<div id="popover-portal" style="position: fixed; top: 0; left: 0;"/>
			<t-select
				title="Fruit"
				width="280px"
				:items="fruits"
				:selectedItem="selectedFruit"
				@select="selectedFruit = $event">
			</t-select>
			<t-select
				appearance="primary"
				width="280px"
				intent="success"
				:items="fruits"
				:selectedItem="selectedFruit"
				@select="selectedFruit = $event">
			</t-select>
			<t-select
				appearance="minimal"
				width="100px"
				:items="fruits"
				:selectedItem="selectedFruit"
				@select="selectedFruit = $event">
			</t-select>
		</div>
	`
});
```

```html
<t-select
	title="Fruit"
	width="260px"
	:items="fruits">
</t-select>
<t-select
	appearance="primary"
	width="260px"
	intent="success"
	:items="fruits"
	:selectedItem="fruits[1]">
</t-select>
<t-select
	appearance="minimal"
	width="100px"
	:items="fruits"
	:selectedItem="fruits[3]">
</t-select>
```

### Disabled

```jsx
<t-select disabled></t-select>
```
</docs>
